import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Plus, Edit2, Trash2, Users, Sliders, CheckCircle, XCircle, Lock, Filter } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useAuthStore } from '../../stores/authStore'
import { useToast } from '../../hooks/useToast'
import filtrosBateriasService from '../../services/filtrosBateriasService'

const TIPO_LABEL = { SEXO: 'Por Sexo', PERSONALIZADO: 'Personalizado' }

const BATERIA_COLORS = {
  SEXO: { bg: 'bg-blue-50', border: 'border-blue-100', icon: 'text-blue-500', text: 'text-blue-700' },
  PERSONALIZADO: { bg: 'bg-purple-50', border: 'border-purple-100', icon: 'text-purple-500', text: 'text-purple-700' },
}

export default function FiltrosAutomaticos() {
  const navigate = useNavigate()
  const { organizadorAtivo } = useOrganizador()
  const { user } = useAuthStore()
  const toast = useToast()
  const qc = useQueryClient()
  const [confirmDelete, setConfirmDelete] = useState(null)

  const isPremium = (organizadorAtivo?.produtos_premium || []).includes('filtros_baterias')

  const canEdit =
    user?.permission_level === 'super_admin' ||
    user?.permission_level === 'admin' ||
    (user?.modulos_acesso || []).some(m => m?.modulo === 'ordem_entrada' && m?.editar_filtros)

  const { data: filtros = [], isLoading } = useQuery(
    ['filtros-baterias', organizadorAtivo?.id],
    () => filtrosBateriasService.listar(organizadorAtivo.id),
    { enabled: !!organizadorAtivo?.id && isPremium }
  )

  const { mutate: excluir } = useMutation(
    (id) => filtrosBateriasService.excluir(organizadorAtivo.id, id),
    {
      onSuccess: () => {
        toast.success('Filtro excluído')
        qc.invalidateQueries(['filtros-baterias', organizadorAtivo?.id])
        setConfirmDelete(null)
      },
      onError: () => toast.error('Erro ao excluir filtro'),
    }
  )

  if (!isPremium) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-10 text-center max-w-md">
          <Lock size={40} className="mx-auto mb-3 text-yellow-500" />
          <h2 className="text-xl font-bold text-yellow-800 mb-2">Recurso Premium</h2>
          <p className="text-yellow-700 text-sm">
            Filtros Automáticos de Baterias está disponível apenas no plano Premium.
            Entre em contato para habilitar este recurso.
          </p>
        </div>
      </div>
    )
  }

  const ativos = filtros.filter(f => f.ativo).length

  return (
    <div className="p-6">
      {/* Cabeçalho */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Filtros Automáticos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Templates reutilizáveis para gerar a ordem de entrada por baterias
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => navigate('/ordem-entrada/filtros-automaticos/novo')}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition shadow-sm"
          >
            <Plus size={16} />
            Novo Filtro
          </button>
        )}
      </div>

      {/* Cards informativos */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-2xl font-bold text-gray-900">{filtros.length}</div>
          <div className="text-xs text-gray-500 mt-0.5">Total de filtros</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-2xl font-bold text-green-700">{ativos}</div>
          <div className="text-xs text-gray-500 mt-0.5">Ativos</div>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
          <Users size={20} className="text-blue-500 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-blue-800">Por Sexo</div>
            <div className="text-xs text-blue-600 mt-0.5 leading-tight">Divide em Fêmeas e Machos automaticamente</div>
          </div>
        </div>
        <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 flex items-start gap-3">
          <Sliders size={20} className="text-purple-500 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-purple-800">Personalizado</div>
            <div className="text-xs text-purple-600 mt-0.5 leading-tight">Monte baterias por tipo, sexo, raça e peso</div>
          </div>
        </div>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-20" />
          ))}
        </div>
      ) : filtros.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
          <Filter size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-600 font-semibold mb-1">Nenhum filtro cadastrado</p>
          <p className="text-sm text-gray-400 mb-5">Crie templates para gerar a ordem de entrada com baterias</p>
          {canEdit && (
            <button
              onClick={() => navigate('/ordem-entrada/filtros-automaticos/novo')}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition"
            >
              <Plus size={15} /> Criar primeiro filtro
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtros.map(f => {
            const c = BATERIA_COLORS[f.tipo] || BATERIA_COLORS.PERSONALIZADO
            return (
              <div
                key={f.id}
                className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-4 hover:shadow-sm transition group"
              >
                {/* Ícone tipo */}
                <div className={`w-10 h-10 rounded-lg ${c.bg} border ${c.border} flex items-center justify-center shrink-0`}>
                  {f.tipo === 'SEXO'
                    ? <Users size={18} className={c.icon} />
                    : <Sliders size={18} className={c.icon} />}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 truncate">{f.nome}</span>
                    {f.ativo ? (
                      <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                        <CheckCircle size={11} /> Ativo
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                        <XCircle size={11} /> Inativo
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className={`text-xs font-medium ${c.text}`}>{TIPO_LABEL[f.tipo]}</span>
                    {f.tipo === 'PERSONALIZADO' && (
                      <span className="text-xs text-gray-400">{f.regras?.length || 0} bateria(s)</span>
                    )}
                    {f.tipo === 'SEXO' && f.sexo_config && (
                      <span className="text-xs text-gray-400">
                        {f.sexo_config.sexo_primeiro === 'MACHO' ? 'Machos' : 'Fêmeas'} primeiro
                      </span>
                    )}
                  </div>
                </div>

                {/* Ações */}
                {canEdit && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button
                      onClick={() => navigate(`/ordem-entrada/filtros-automaticos/${f.id}`)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      title="Editar"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={() => setConfirmDelete(f)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Excluir"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Modal confirmar exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-bold text-gray-900 text-lg mb-2">Excluir filtro?</h3>
            <p className="text-gray-600 text-sm mb-5">
              O filtro <strong>{confirmDelete.nome}</strong> será excluído permanentemente.
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg font-medium text-sm hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={() => excluir(confirmDelete.id)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium text-sm transition"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
